/**
 * Feedback: a floating button a client's site loads (`web/public/feedback.js`,
 * as a script tag, a bookmarklet or the WordPress plugin), which sends what was
 * typed and the page's URL to `POST /api/feedback/:key`. The key names where it
 * lands — a team and a repo, and optionally a task to go under — and becomes an
 * ordinary task there. The board carries the same button for itself, which makes
 * the task over the cookie; an admin may name a task its feedback goes under too.
 *
 * Shared so the public route and the board's own button title and word a task
 * the same way.
 */

/** Public, cookie-less: the key is in the path, and it is the whole of the secret. */
export const FEEDBACK_PREFIX = "/api/feedback";

/** Longest feedback taken, and the longest URL kept with it. */
export const FEEDBACK_MAX = 5000;
export const FEEDBACK_URL_MAX = 2000;
export const FEEDBACK_LABEL_MAX = 80;
/** Longest email address kept (RFC 5321's path limit). */
export const FEEDBACK_EMAIL_MAX = 254;

/** Keys are hex, so one can go into a URL, a script tag and a PHP string unescaped. */
export const FEEDBACK_KEY = /^[0-9a-f]{32}$/;

export const FEEDBACK_CORNERS = ["bottom-right", "bottom-left", "top-right", "top-left"] as const;
export type FeedbackCorner = (typeof FEEDBACK_CORNERS)[number];

/** Where a key's feedback goes. Admin-made, in its repo's settings; all listed in Global settings. */
export interface FeedbackKey {
  key: string;
  /** Its topic — "Acme shop"; said in each task's notes. */
  label: string;
  teamId: string;
  repo: string | null;
  /** The task each piece of feedback becomes a sub-task of; null is a root of
   *  its own. One that has gone, or left the key's team, counts as null. */
  parentId: string | null;
  /** Whether its button asks for the sender's email (optional to give). Set when
   *  the key is made, since it is baked into the snippets as `data-email`. */
  askEmail: boolean;
  createdBy: string | null;
  createdAt: number;
  uses: number;
  lastUsedAt: number | null;
}

/** Longest title kept; the widget's title box stops there too. */
export const FEEDBACK_TITLE_MAX = 80;

/** The address the sender left, if it looks like one; anything else is dropped,
 *  never refused — it was optional. */
export function feedbackEmail(value: unknown): string {
  const email = typeof value === "string" ? value.trim() : "";
  return email.length <= FEEDBACK_EMAIL_MAX && /^[^\s@]+@[^\s@]+$/.test(email) ? email : "";
}

/** Hidden fields a page sends with its feedback (a build number, a user id, a
 *  version): at most this many, names and values cut to these lengths. */
export const FEEDBACK_FIELDS_MAX = 20;
export const FEEDBACK_FIELD_NAME_MAX = 40;
export const FEEDBACK_FIELD_VALUE_MAX = 500;

/** The hidden fields worth keeping, in the order sent: strings, numbers and
 *  booleans, each on one line; anything else (an object, a blank) is dropped,
 *  never refused — the sender never saw them. */
export function feedbackFields(value: unknown): [string, string][] {
  if (!value || typeof value !== "object" || Array.isArray(value)) return [];
  const out: [string, string][] = [];
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (out.length >= FEEDBACK_FIELDS_MAX) break;
    if (typeof v !== "string" && typeof v !== "number" && typeof v !== "boolean") continue;
    const name = k.replace(/\s+/g, " ").trim().slice(0, FEEDBACK_FIELD_NAME_MAX);
    const val = String(v).replace(/\s+/g, " ").trim().slice(0, FEEDBACK_FIELD_VALUE_MAX);
    if (name && val) out.push([name, val]);
  }
  return out;
}

/** A task's title and notes for a piece of feedback: titled by what was typed in
 *  the title box, else (a script from before it had one) by the first line; the
 *  whole of it in the notes with where it was sent from, who, if they said, and
 *  the page's hidden fields (`feedbackFields()`), one `name: value` line each. */
export function feedbackTask(
  text: string,
  url: string,
  from: string | null,
  title = "",
  email = "",
  fields: [string, string][] = [],
): { title: string; notes: string } {
  const body = text.trim();
  const named = title.replace(/\s+/g, " ").trim();
  const first = named || (body.split("\n").map((l) => l.trim()).find(Boolean) ?? "Feedback");
  const clipped = first.length > FEEDBACK_TITLE_MAX ? `${first.slice(0, FEEDBACK_TITLE_MAX - 1).trimEnd()}…` : first;
  const where = [from ? `Feedback from ${from}` : "Feedback", url ? `on ${url}` : ""].filter(Boolean).join(" ");
  const reply = email ? `\n— Reply to ${email}` : "";
  const extra = fields.map(([k, v]) => `\n${k}: ${v}`).join("");
  return { title: clipped, notes: `${body}\n\n— ${where}${reply}${extra}` };
}
